import type { UserLanguage, UserRole, UserStatus } from '@invoice/shared/schemas';
import { type ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

const STORAGE_KEY = 'invoice.language';

type Params = Record<string, string | number>;
type MessageValue = string | ((params: Params) => string);

const messages = {
  de: {
    'shell.nav.invoices': 'Rechnungen',
    'shell.nav.rules': 'Regeln',
    'shell.nav.admin': 'Admin',
    'shell.nav.orderConfirmations': 'Auftragsbestätigungen',
    'shell.nav.customerOrders': 'Kundenbestellungen',
    'shell.section.supplierInvoices': 'Lieferantenrechnung',
    'shell.section.orderConfirmations': 'Auftragsbestätigungen',
    'shell.section.customerOrders': 'Kundenbestellungen',
    'shell.section.admin': 'Admin',
    'shell.sidebar.open': 'Sidebar öffnen',
    'shell.sidebar.close': 'Sidebar schließen',
    'shell.notifications': 'Benachrichtigungen',
    'shell.notificationsPage': 'Benachrichtigungen anzeigen',
    'shell.mobileMenu': 'Mobiles Menü',
    'shell.mobileNav': 'Mobile Navigation',
    'shell.mainNav': 'Hauptnavigation',
    'shell.userMenu': 'Benutzermenü',
    'shell.userMenuOptions': 'Benutzermenü Optionen',
    'shell.settings': 'Einstellungen',
    'shell.logout': 'Logout',
    'shell.logoutPending': 'Logout...',
    'shell.sourceOfTruth': 'Source of truth:',
    'shell.build': ({ mode }: Params) => `Invoice Automation Build ${mode}`,
    'auth.redirectTitle': 'Weiterleitung zur Anmeldung…',
    'auth.redirectMessage':
      'Bitte melde dich an, um diese Ansicht zu laden. Du wirst weitergeleitet.',
    'notFound.title': 'Seite nicht gefunden',
    'notFound.description':
      'Die angeforderte Route existiert nicht. Wechsle zu Rechnungen, um fortzufahren.',
    'login.title': 'Anmelden',
    'login.description': 'Nach erfolgreicher Anmeldung landest du direkt in der Rechnungsansicht.',
    'login.email': 'E-Mail',
    'login.password': 'Passwort',
    'login.submit': 'Anmelden',
    'login.invalidEmail': 'Bitte eine gültige E-Mail-Adresse eingeben',
    'login.passwordRequired': 'Passwort ist erforderlich',
    'login.showPassword': 'Passwort anzeigen',
    'login.hidePassword': 'Passwort verbergen',
    'login.mfaUnsupported':
      'Dieser Benutzer braucht MFA. Der Abschluss des MFA-Flows ist in der Web-App noch nicht verdrahtet.',
    'login.seedUser': 'Lokaler Seed-Benutzer:',
    'settings.sectionLabel': 'Einstellungen',
    'settings.pageTitle': 'Einstellungen',
    'settings.pageDescription':
      'Verwalte deine benutzerbezogenen Einstellungen und deine bevorzugte Sprache.',
    'settings.loading': 'Einstellungen werden geladen...',
    'settings.unauthorized': 'Bitte melde dich an, um deine Einstellungen zu laden.',
    'settings.accountTitle': 'Mein Konto',
    'settings.accountDescription': 'Deine Benutzerinformationen und persönlichen Voreinstellungen.',
    'settings.languageTitle': 'Sprache',
    'settings.languageDescription':
      'Diese Sprache wird für deine Web-Oberfläche als persönliche Voreinstellung gespeichert.',
    'settings.languageLabel': 'Oberflächensprache',
    'settings.languageOption.de': 'Deutsch',
    'settings.languageOption.en': 'Englisch',
    'settings.defaultTenantTitle': 'Standard-Tenant',
    'settings.defaultTenantDescription':
      'Legt fest, welcher Tenant in tenantbezogenen Ansichten automatisch vorausgewählt wird.',
    'settings.defaultTenantLabel': 'Standard-Tenant',
    'settings.defaultTenantEmpty': 'Kein Tenant verfügbar',
    'settings.defaultTenantAdminHint':
      'Administratoren starten bewusst ohne vorausgewählten Tenant.',
    'settings.saveSuccess': 'Einstellungen gespeichert.',
    'settings.saveFailed': 'Speichern fehlgeschlagen.',
    'settings.save': 'Speichern',
    'settings.saving': 'Speichert...',
    'modulePlaceholder.authRequired': 'Bitte melde dich an, um diesen Bereich zu laden.',
    'modulePlaceholder.loading': 'Bereich wird geladen...',
    'orderConfirmations.sectionLabel': 'Workspace',
    'orderConfirmations.pageTitle': 'Auftragsbestätigungen',
    'orderConfirmations.pageDescription':
      'Eingehende Auftragsbestätigungen mit SAP-Prüfung gegen Normalbestellung ZEL1, Lieferadresse, Positionen und Materialstamm.',
    'orderConfirmations.newConfirmation': 'Neue Auftragsbestätigung',
    'orderConfirmations.newConfirmationAria': 'Neue Auftragsbestätigung hochladen',
    'orderConfirmations.loadingTitle': 'Lade Auftragsbestätigungen…',
    'orderConfirmations.count': ({ count }: Params) =>
      `${count} Auftragsbestätigung${Number(count) === 1 ? '' : 'en'}`,
    'orderConfirmations.loadError': 'Fehler beim Laden',
    'orderConfirmations.errorTitle': 'Auftragsbestätigungen konnten nicht geladen werden',
    'orderConfirmationRules.sectionLabel': 'Regeln',
    'orderConfirmationRules.pageTitle': 'Regeln für Auftragsbestätigungen',
    'orderConfirmationRules.pageDescription':
      'Regeln für die Verarbeitung von Auftragsbestätigungen werden hier gebündelt.',
    'customerOrders.sectionLabel': 'Workspace',
    'customerOrders.pageTitle': 'Kundenbestellungen',
    'customerOrders.pageDescription':
      'Eingehende Kundenbestellungen mit LLM-Extraktion, SAP-Kundensuche, KNMT-Materialabgleich und VA01-Anlage als ZAN1-Terminauftrag.',
    'customerOrders.newOrder': 'Neue Kundenbestellung',
    'customerOrders.newOrderAria': 'Neue Kundenbestellung hochladen',
    'customerOrders.loadingTitle': 'Lade Kundenbestellungen…',
    'customerOrders.count': ({ count }: Params) =>
      `${count} Kundenbestellung${Number(count) === 1 ? '' : 'en'}`,
    'customerOrders.loadError': 'Fehler beim Laden',
    'customerOrders.errorTitle': 'Kundenbestellungen konnten nicht geladen werden',
    'customerOrdersTable.searchLabel': 'Kunde suchen',
    'customerOrdersTable.searchPlaceholder': 'z. B. Muster AG',
    'customerOrdersTable.columnNumber': 'Bestellnummer',
    'customerOrdersTable.columnCustomer': 'Kunde',
    'customerOrdersTable.columnAmount': 'Auftragswert',
    'customerOrdersTable.columnTradingMargin': 'Handelsspanne',
    'customerOrdersTable.columnIssueDate': 'Bestelldatum',
    'customerOrdersTable.emptyTitle': 'Noch keine Kundenbestellungen',
    'customerOrdersTable.emptyDescription':
      'Lade eine Kundenbestellung hoch, um Felder per LLM auszulesen und die SAP-Anlage vorzubereiten.',
    'customerOrdersTable.selectAllAria': 'Alle Kundenbestellungen auswählen',
    'customerOrdersTable.selectOrderAria': ({ order }: Params) =>
      `Kundenbestellung ${order} auswählen`,
    'customerOrdersTable.selectionCount': ({ count }: Params) =>
      `${count} Kundenbestellung${Number(count) === 1 ? '' : 'en'} ausgewählt`,
    'customerOrdersTable.bulkDeleteTitle': 'Kundenbestellungen löschen',
    'customerOrdersTable.bulkDeleteDescription': ({ count }: Params) =>
      `${count} Kundenbestellung${Number(count) === 1 ? '' : 'en'} wirklich löschen?`,
    'customerOrderRules.sectionLabel': 'Regeln',
    'customerOrderRules.pageTitle': 'Regeln für Kundenbestellungen',
    'customerOrderRules.pageDescription':
      'Regeln für die Verarbeitung von Kundenbestellungen werden hier gebündelt.',
    'rules.sectionLabel': 'Regeln',
    'rules.pageTitle': 'Mapping & Regeln',
    'rules.pageDescription':
      'Tenant-generische und lieferantenspezifische Regeln steuern die LLM-Extraktion. Änderungen werden versioniert und sind über das Versionen-Panel zurückholbar.',
    'rules.create': 'Neue Regel',
    'rules.createDisabledNoTenant': 'Bitte zuerst einen Tenant wählen.',
    'rules.tab.generic': 'Tenant-generisch',
    'rules.tab.customer': 'Lieferantenspezifisch',
    'rules.tenantLabel': 'Tenant',
    'rules.supplierSearchLabel': 'Lieferant suchen',
    'rules.tenantPlaceholder': 'Tenantname suchen',
    'rules.tenantLoading': 'Tenants werden gesucht...',
    'rules.tenantEmpty': 'Keine Tenants gefunden.',
    'rules.tenantMinSearch':
      'In das Feld klicken, um alle Tenants anzuzeigen, oder tippen zum Filtern.',
    'rules.supplierIdleHint':
      'In das Feld klicken, um alle Lieferanten anzuzeigen, oder tippen zum Filtern.',
    'rules.loading': 'Lade Regeln…',
    'rules.count': ({ count }: Params) => `${count} Regel${Number(count) === 1 ? '' : 'n'}`,
    'rules.loadError': 'Fehler beim Laden',
    'rules.selectTenantHint': 'Bitte einen Tenant wählen, um die Regeln anzuzeigen.',
    'rules.deleteConfirm': ({ title }: Params) => `Regel "${title}" wirklich löschen?`,
    'rulesTable.title': 'Titel',
    'rulesTable.tenant': 'Tenant',
    'rulesTable.status': 'Status',
    'rulesTable.version': 'Version',
    'rulesTable.lastEditor': 'Letzter Editor',
    'rulesTable.updated': 'Aktualisiert',
    'rulesTable.actions': 'Aktionen',
    'rulesTable.active': 'Aktiv',
    'rulesTable.inactive': 'Inaktiv',
    'rulesTable.activate': 'Aktivieren',
    'rulesTable.deactivate': 'Deaktivieren',
    'rulesTable.edit': 'Bearbeiten',
    'rulesTable.testRun': 'Testlauf',
    'rulesTable.delete': 'Löschen',
    'rulesTable.editAria': ({ title }: Params) => `Regel ${title} bearbeiten`,
    'rulesTable.testRunAria': ({ title }: Params) => `Regel ${title} testen`,
    'rulesTable.deleteAria': ({ title }: Params) => `Regel ${title} löschen`,
    'rulesTable.toggleActiveAria': 'Regel aktivieren',
    'rulesTable.toggleInactiveAria': 'Regel deaktivieren',
    'rulesTable.emptyTitle': 'Noch keine Regeln',
    'rulesTable.emptyDescription': 'Lege die erste Regel an, um die Extraktion zu konfigurieren.',
    'ruleDialog.headerEdit': ({ version }: Params) => `Regel bearbeiten – v${version}`,
    'ruleDialog.headerCreate': 'Neue Regel',
    'ruleDialog.descriptionEdit':
      'Bearbeitet die aktive Fassung. Eine Vorgänger-Version wird automatisch im Verlauf gespeichert.',
    'ruleDialog.descriptionCreate':
      'Erzeugt eine neue Regel. Lieferantenspezifische Regeln überschreiben tenant-generische Regeln auf Konflikt.',
    'ruleDialog.tabEdit': 'Bearbeiten',
    'ruleDialog.tabVersions': 'Versionen',
    'ruleDialog.scopeLabel': 'Geltungsbereich',
    'ruleDialog.scopeGeneric': 'Tenant-generisch',
    'ruleDialog.scopeCustomer': 'Lieferantenspezifisch',
    'ruleDialog.supplierLabel': 'Lieferant',
    'ruleDialog.supplierPlaceholder': 'Lieferantenname suchen',
    'ruleDialog.supplierIdleHint':
      'Wähle zuerst einen Tenant; danach zeigt das Feld alle bekannten Lieferanten an.',
    'ruleDialog.supplierLoading': 'Lieferanten werden geladen...',
    'ruleDialog.supplierEmpty': 'Keine Lieferanten gefunden.',
    'ruleDialog.supplierClearAria': 'Lieferant leeren',
    'ruleDialog.titleLabel': 'Titel *',
    'ruleDialog.bodyLabel': 'Inhalt *',
    'ruleDialog.bodyAria': 'Regel-Inhalt',
    'ruleDialog.characters': ({ count, max }: Params) =>
      `${Number(count).toLocaleString('de-DE')} / ${Number(max).toLocaleString('de-DE')} Zeichen`,
    'ruleDialog.activeLabel': 'Aktiv',
    'ruleDialog.delete': 'Löschen',
    'ruleDialog.testRun': 'Testlauf',
    'ruleDialog.cancel': 'Abbrechen',
    'ruleDialog.save': 'Speichern',
    'ruleDialog.create': 'Anlegen',
    'ruleDialog.deleteTitle': 'Regel löschen?',
    'ruleDialog.deleteDescription':
      'Die Regel wird auf inaktiv gesetzt und aus der aktiven Bearbeitung entfernt.',
    'ruleDialog.titleRequired': 'Titel ist erforderlich',
    'ruleDialog.titleMax': 'Maximal 200 Zeichen',
    'ruleDialog.bodyRequired': 'Inhalt ist erforderlich',
    'ruleDialog.bodyMax': ({ max }: Params) => `Maximal ${max} Zeichen`,
    'ruleDialog.tenantRequired': 'Tenant ist erforderlich',
    'ruleDialog.supplierRequired': 'Lieferant ist erforderlich',
    'upload.title': 'Rechnungen hochladen',
    'upload.description': 'PDF oder XML. Maximal 20 MB pro Datei. Mehrere Dateien möglich.',
    'upload.orderConfirmationTitle': 'Auftragsbestätigungen hochladen',
    'upload.orderConfirmationDescription':
      'PDF oder XML. Die Verarbeitung prüft die Bestellnummer, Positionen, Lieferadresse und Materialnummern gegen SAP.',
    'upload.customerOrderTitle': 'Kundenbestellungen hochladen',
    'upload.customerOrderDescription':
      'PDF oder XML. Die Verarbeitung liest Kundenbestellungen aus, berechnet die Handelsspanne und bereitet die VA01-Anlage vor.',
    'upload.tenantLabel': 'Tenant',
    'upload.tenantSelectAuto': 'Tenant wird automatisch gewählt',
    'upload.tenantSelectPlaceholder': 'Tenant auswählen',
    'upload.tenantAutoHint':
      'Wenn kein Tenant ausgewählt wird, verwendet der Upload den einzigen verfügbaren Tenant automatisch.',
    'upload.tenantLoadError': ({ message }: Params) =>
      `Tenants konnten nicht geladen werden: ${message}`,
    'upload.tenantRequiredHint': 'Bitte vor der Verarbeitung einen Tenant auswählen.',
    'upload.tenantRequiredFlash':
      'Bitte zuerst einen Tenant auswählen oder den einzigen verfügbaren Tenant verwenden.',
    'upload.fileInputLabel': 'Dateien auswählen',
    'upload.fileListLabel': 'Hochzuladende Dateien',
    'upload.cancel': 'Abbrechen',
    'upload.processCount': ({ count }: Params) => `Verarbeiten (${count})`,
    'upload.pickFiles': 'Dateien auswählen',
    'upload.dropTitle': 'Dateien hier ablegen',
    'upload.dropDescription': 'PDF, XML – maximal 20 MB pro Datei',
    'upload.closeRunningTitle': 'Upload abbrechen?',
    'upload.closeRunningDescription':
      'Es laufen noch Uploads. Wenn du den Dialog jetzt schließt, wird die lokale Übersicht verworfen.',
    'upload.closeRunningConfirm': 'Upload abbrechen',
    'upload.closeRunningStay': 'Weiter hochladen',
    'upload.flashSuccess': ({ successCount }: Params) =>
      `${successCount} Datei(en) erfolgreich hochgeladen.`,
    'upload.flashError': ({ errorCount }: Params) =>
      `${errorCount} Datei(en) sind fehlgeschlagen. Mit "Erneut versuchen" wiederholen.`,
    'upload.flashMixed': ({ successCount, errorCount }: Params) =>
      `${successCount} hochgeladen, ${errorCount} fehlgeschlagen. Fehlgeschlagene Dateien können erneut versucht werden.`,
    'upload.invalidFile': 'Ungültige Datei',
    'uploadRow.retryAria': ({ name }: Params) => `Erneut versuchen: ${name}`,
    'uploadRow.removeAria': ({ name }: Params) => `Entfernen: ${name}`,
    'uploadRow.phaseQueued': 'Bereit',
    'uploadRow.phaseUploading': 'Lädt …',
    'uploadRow.phaseExisting': 'Bereits vorhanden',
    'uploadRow.phaseUploaded': 'Hochgeladen',
    'uploadRow.phaseError': 'Fehler',
    'invoiceList.sectionLabel': 'Workspace',
    'invoiceList.pageTitle': 'Rechnungen',
    'invoiceList.pageDescription':
      'Alle eingegangenen Rechnungen mit Status, Lieferant und Betrag. Filter und Sortierung werden in der URL gespeichert und bleiben beim Teilen erhalten.',
    'invoiceList.newInvoice': 'Neue Rechnung',
    'invoiceList.newInvoiceAria': 'Neue Rechnung hochladen',
    'invoiceList.loadingTitle': 'Lade Rechnungen…',
    'invoiceList.count': ({ count }: Params) => `${count} Rechnungen`,
    'invoiceList.loadError': 'Fehler beim Laden',
    'invoiceList.errorTitle': 'Rechnungen konnten nicht geladen werden',
    'invoiceList.retry': 'Erneut versuchen',
    'invoiceListTable.columnNumber': 'Rechnungsnummer',
    'invoiceListTable.columnDocumentType': 'Dokumenttyp',
    'invoiceListTable.columnTenant': 'Tenant',
    'invoiceListTable.columnSupplier': 'Lieferant',
    'invoiceListTable.columnAmount': 'Betrag',
    'invoiceListTable.columnTradingMargin': 'Handelsspanne',
    'invoiceListTable.columnIssueDate': 'Rechnungsdatum',
    'invoiceListTable.columnStatus': 'Status',
    'invoiceListTable.columnUploaded': 'Hochgeladen',
    'invoiceListTable.skontoUntil': ({ date }: Params) => `Skonto bis ${date}`,
    'invoiceListTable.columnActions': 'Aktionen',
    'invoiceListTable.selectAllAria': 'Alle löschbaren Rechnungen auswählen',
    'invoiceListTable.selectInvoiceAria': ({ invoice }: Params) => `Rechnung ${invoice} auswählen`,
    'invoiceListTable.selectionCount': ({ count }: Params) =>
      `${count} Rechnung${Number(count) === 1 ? '' : 'en'} ausgewählt`,
    'invoiceListTable.clearSelection': 'Auswahl aufheben',
    'invoiceListTable.bulkDeleteAction': 'Ausgewählte löschen',
    'invoiceListTable.bulkDeleteTitle': 'Ausgewählte Rechnungen löschen?',
    'invoiceListTable.bulkDeleteDescription': ({ count }: Params) =>
      `Dieser Schritt entfernt ${count} Rechnung${Number(count) === 1 ? '' : 'en'} dauerhaft.`,
    'invoiceListTable.sortBy': ({ label }: Params) => `Sortieren nach ${label}`,
    'invoiceListTable.unknownNumber': 'unbekannt',
    'invoiceListTable.documentTypeInvoice': 'Rechnung',
    'invoiceListTable.documentTypeOrderConfirmation': 'Auftragsbestätigung',
    'invoiceListTable.documentTypeUnknown': 'Unbekannt',
    'invoiceListTable.emptyTitle': 'Noch keine Rechnungen',
    'invoiceListTable.emptyDescription':
      'Sobald die ersten Rechnungen hochgeladen wurden, erscheinen sie hier mit Status, Betrag und Lieferant.',
    'invoiceListTable.deleteTitle': 'Rechnung löschen?',
    'invoiceListTable.deleteDescription':
      'Dieser Schritt entfernt die Datei und den Datensatz dauerhaft.',
    'invoiceListTable.deleteAction': 'Löschen',
    'invoiceListTable.deleteAria': ({ invoice }: Params) => `Rechnung ${invoice} löschen`,
    'orderConfirmationsTable.columnNumber': 'Bestätigungsnummer',
    'orderConfirmationsTable.columnIssueDate': 'Bestätigungsdatum',
    'orderConfirmationsTable.emptyTitle': 'Noch keine Auftragsbestätigungen',
    'orderConfirmationsTable.emptyDescription':
      'Sobald Auftragsbestätigungen per E-Mail oder Upload verarbeitet wurden, erscheinen sie hier mit Status, Betrag und Lieferant.',
    'orderConfirmationsTable.selectAllAria': 'Alle löschbaren Auftragsbestätigungen auswählen',
    'orderConfirmationsTable.selectConfirmationAria': ({ confirmation }: Params) =>
      `Auftragsbestätigung ${confirmation} auswählen`,
    'orderConfirmationsTable.selectionCount': ({ count }: Params) =>
      `${count} Auftragsbestätigung${Number(count) === 1 ? '' : 'en'} ausgewählt`,
    'orderConfirmationsTable.bulkDeleteTitle': 'Ausgewählte Auftragsbestätigungen löschen?',
    'orderConfirmationsTable.bulkDeleteDescription': ({ count }: Params) =>
      `Dieser Schritt entfernt ${count} Auftragsbestätigung${Number(count) === 1 ? '' : 'en'} dauerhaft.`,
    'comments.title': 'Kommentare',
    'comments.loading': 'Lade Kommentare...',
    'comments.empty': 'Noch keine Kommentare.',
    'comments.newLabel': 'Neuer Kommentar',
    'comments.placeholder': 'Markdown unterstützt: **fett**, *kursiv*, `code`, [link](https://...)',
    'comments.publish': 'Veröffentlichen',
    'comments.characters': ({ count, max }: Params) => `${count} / ${max}`,
    'comments.errorEmpty': 'Bitte einen Kommentar eingeben.',
    'comments.errorSave': 'Kommentar konnte nicht gespeichert werden.',
    'comments.errorDelete': 'Kommentar konnte nicht gelöscht werden.',
    'comments.errorForbiddenDelete': 'Nur der Autor oder ein Admin darf diesen Kommentar löschen.',
    'comments.deleteAria': 'Kommentar löschen',
    'comments.delete': 'Löschen',
    'invoiceEdit.unsavedChanges': 'Ungespeicherte Änderungen',
    'invoiceEdit.allSaved': 'Alles gespeichert',
    'invoiceEdit.save': 'Speichern',
    'invoiceEdit.saveAria': 'Änderungen speichern',
    'invoiceEdit.metaSection': 'Belegdaten',
    'invoiceEdit.documentTypeLabel': 'Belegtyp',
    'invoiceEdit.documentTypeInvoice': 'Rechnung',
    'invoiceEdit.documentTypeOrderConfirmation': 'Auftragsbestätigung',
    'invoiceEdit.orderNumberLabelInvoice': 'Eigene Bestellnummer',
    'invoiceEdit.orderNumberLabelOrderConfirmation': 'Bestellnummer',
    'invoiceEdit.orderDateLabel': 'Datum der Bestellung',
    'invoiceEdit.documentNumberLabelInvoice': 'Rechnungsnummer',
    'invoiceEdit.documentNumberLabelOrderConfirmation': 'Auftragsbestätigungsnummer',
    'invoiceEdit.issueDateLabel': 'Belegdatum',
    'invoiceEdit.dueDateLabel': 'Fälligkeitsdatum',
    'invoiceEdit.deliveryDateLabel': 'Lieferdatum',
    'invoiceEdit.currencyLabel': 'Währung',
    'invoiceEdit.paymentSection': 'Zahlungsbedingungen & Skonto',
    'invoiceEdit.deliveryTermsSection': 'Lieferbedingungen',
    'invoiceEdit.paymentTermsLabel': 'Zahlungsbedingungen',
    'invoiceEdit.discountDeadlineLabel': 'Skonto bis',
    'invoiceEdit.discountPercentLabel': 'Skonto in %',
    'invoiceEdit.paymentDueDateLabel': 'Ohne Abzug bis',
    'invoiceEdit.discountAmountLabel': 'Skonto in €',
    'invoiceEdit.amountAfterDiscountLabel': 'Endbetrag nach Skonto',
    'invoiceEdit.incotermsLabel': 'Incoterms',
    'invoiceEdit.sellerSection': 'Lieferant',
    'invoiceEdit.buyerSection': 'Käufer',
    'invoiceEdit.nameLabel': 'Name',
    'invoiceEdit.streetLabel': 'Straße',
    'invoiceEdit.postalCodeLabel': 'PLZ',
    'invoiceEdit.cityLabel': 'Ort',
    'invoiceEdit.countryLabel': 'Land (ISO-2)',
    'invoiceEdit.countryPlaceholder': 'DE',
    'invoiceEdit.vatIdLabel': 'USt-ID',
    'invoiceEdit.vatIdPlaceholder': 'DE123456789',
    'invoiceEdit.sapSupplierIdLabel': 'SAP-Lieferanten-ID',
    'invoiceEdit.sapCustomerIdLabel': 'SAP-Kunden-ID',
    'invoiceEdit.sapChecking': 'SAP-Lieferant wird geprüft...',
    'invoiceEdit.sapMapped': ({ id }: Params) => `Aus vorhandener SAP-Zuordnung übernommen: ${id}`,
    'invoiceEdit.sapNotFound':
      'Keine vorhandene SAP-Zuordnung gefunden. Bitte Lieferanten-ID manuell ergänzen.',
    'invoiceEdit.itemsSection': 'Positionen',
    'invoiceEdit.ancillaryItemsSection': 'Nebenkosten Positionen',
    'invoiceEdit.addPosition': 'Position hinzufügen',
    'invoiceEdit.addAncillaryPosition': 'Nebenkostenposition hinzufügen',
    'invoiceEdit.noItems': 'Keine Positionen erfasst.',
    'invoiceEdit.noAncillaryItems': 'Keine Nebenkostenpositionen erfasst.',
    'invoiceEdit.positionNumber': ({ index }: Params) => `Position ${index}`,
    'invoiceEdit.removePositionAria': ({ index }: Params) => `Position ${index} entfernen`,
    'invoiceEdit.positionLabel': 'Pos.',
    'invoiceEdit.itemTypeLabel': 'Art der Position',
    'invoiceEdit.itemTypeStandard': 'Standardposition',
    'invoiceEdit.itemTypeAncillary': 'Nebenkostenposition',
    'invoiceEdit.articleNumberLabel': 'Materialnummer',
    'invoiceEdit.sapMaterialNumberLabel': 'SAP-Materialnummer',
    'invoiceEdit.sapMaterialMissing': 'SAP-Material fehlt:',
    'invoiceEdit.sapMaterialMultiple': 'SAP-Material ist mehrdeutig:',
    'invoiceEdit.descriptionLabel': 'Beschreibung',
    'invoiceEdit.quantityLabel': 'Menge',
    'invoiceEdit.unitPriceLabel': 'Einzelpreis',
    'invoiceEdit.lineDiscountPercentLabel': 'Rabatt in %',
    'invoiceEdit.discountAmountLineLabel': 'Rabatt',
    'invoiceEdit.vatRateLabel': 'Steuersatz %',
    'invoiceEdit.netLabel': 'Netto',
    'invoiceEdit.totalsSection': 'Summen',
    'invoiceEdit.vatLabel': 'MwSt',
    'invoiceEdit.grossLabel': 'Brutto',
    'invoiceEdit.fieldRequired': 'Pflichtfeld fehlt',
    'invoiceEdit.fieldInvalid': 'Ungültiger Wert',
    'invoiceEdit.numberInvalid': 'Bitte eine gültige Zahl eingeben',
    'invoiceEdit.dateInvalid': 'Bitte ein gültiges Datum eingeben',
    'invoiceEdit.vatIdInvalid': 'USt-ID Format ungültig',
    'invoiceEdit.ibanInvalidFormat':
      'IBAN-Format ungültig (Ländercode + 2 Prüfziffern + BBAN erwartet)',
    'invoiceEdit.ibanInvalidCountryLength': 'IBAN-Länge passt nicht zum Land',
    'invoiceEdit.ibanInvalidChecksum': 'IBAN-Prüfsumme stimmt nicht (MOD-97)',
    'invoiceEdit.ibanInvalid': 'IBAN ist ungültig',
    'supplierRule.button': 'Regel hinzufügen',
    'supplierRule.scopeTitle': 'Lieferantenspezifische Regel',
    'supplierRule.closeAria': 'Schließen',
    'supplierRule.placeholder': 'Beschreibe hier die lieferantenspezifische Regel für den Prompt.',
    'supplierRule.cancel': 'Abbrechen',
    'supplierRule.save': 'Speichern',
    'supplierRule.saving': 'Speichert...',
    'supplierRule.saveError': 'Regel konnte nicht gespeichert werden.',
    'supplierRule.title': ({ supplierName }: Params) => `Lieferantenregel – ${supplierName}`,
    'notifications.sectionLabel': 'Benachrichtigungen',
    'notifications.pageTitle': 'Benachrichtigungen',
    'notifications.pageDescription':
      'Alle manuellen Änderungen und Verarbeitungsfehler für deine sichtbaren Tenants.',
    'notifications.loading': 'Benachrichtigungen werden geladen...',
    'notifications.emptyTitle': 'Noch keine Benachrichtigungen',
    'notifications.emptyDescription':
      'Sobald Fehler oder manuelle Rechnungsanpassungen entstehen, erscheinen sie hier.',
    'notifications.loadError': 'Benachrichtigungen konnten nicht geladen werden.',
    'notifications.dropdownTitle': 'Letzte Benachrichtigungen',
    'notifications.dropdownEmpty': 'Keine neuen Einträge.',
    'notifications.searchLabel': 'Suche',
    'notifications.searchPlaceholder': 'Nach Rechnung, Tenant oder Text suchen',
    'notifications.tenantFilterLabel': 'Tenant',
    'notifications.tenantFilter.all': 'Alle Tenants',
    'notifications.typeFilterLabel': 'Typ',
    'notifications.typeFilter.all': 'Alle Typen',
    'notifications.typeFilter.invoice_processed': 'Verarbeitet',
    'notifications.typeFilter.invoice_failed': 'Fehlgeschlagen',
    'notifications.typeFilter.invoice_manual_review': 'Manuelle Prüfung',
    'notifications.typeFilter.invoice_comment_created': 'Kommentar',
    'notifications.typeFilter.invoice_updated': 'Manuelle Anpassung',
    'notifications.typeFilter.invoice_status_changed': 'Statusänderung',
    'notifications.typeFilter.invoice_skonto_reminder': 'Skonto-Reminder',
    'notifications.typeFilter.rule_applied': 'Regel angewendet',
    'notifications.typeFilter.system': 'System',
    'notifications.viewAll': 'Alle ansehen',
    'notifications.clearAll': 'Leeren',
    'notifications.clearing': 'Leert...',
    'notifications.delete': 'Löschen',
    'notifications.deleting': 'Löscht...',
    'notifications.level.info': 'Info',
    'notifications.level.error': 'Fehler',
    'notifications.level.warning': 'Prüfung',
    'notifications.read': 'Gelesen',
    'notifications.unread': 'Ungelesen',
    'notifications.openInvoice': 'Rechnung öffnen',
    'notifications.someone': 'Jemand',
    'notifications.invoiceUpdatedTitle': 'Manuelle Rechnungsanpassung',
    'notifications.invoiceUpdatedMessage': ({ actor, invoice }: Params) =>
      `${actor} hat ${invoice} manuell angepasst.`,
    'notifications.commentCreatedTitle': 'Neuer Kommentar',
    'notifications.commentCreatedMessage': ({ actor, invoice }: Params) =>
      `${actor} hat ${invoice} kommentiert.`,
    'notifications.statusChangedTitle': 'Status manuell geändert',
    'notifications.statusChangedMessage': ({ actor, from, to }: Params) =>
      `${actor} hat den Status von ${from} auf ${to} geändert.`,
    'notifications.manualReviewTitle': 'Manuelle Prüfung erforderlich',
    'notifications.manualReviewMessage': ({ invoice, details }: Params) =>
      `${invoice} benötigt manuelle Prüfung.${details ? ` ${details}` : ''}`,
    'notifications.failedTitle': 'Verarbeitung fehlgeschlagen',
    'notifications.failedMessage': ({ invoice, details }: Params) =>
      `${invoice} konnte nicht verarbeitet werden.${details ? ` ${details}` : ''}`,
    'notifications.skontoReminderTitle': 'Skonto-Frist läuft aus',
    'notifications.skontoReminderMessage': ({ invoice, deadline }: Params) =>
      `${invoice} sollte bis ${deadline} bezahlt werden, um Skonto zu sichern.`,
    'common.name': 'Name',
    'common.email': 'E-Mail',
    'common.role': 'Rolle',
    'common.status': 'Status',
    'common.language': 'Sprache',
    'common.selected': 'Ausgewählt',
    'common.clear': 'leeren',
    'admin.forbidden': 'Diese Ansicht ist nur für Admins freigegeben.',
    'admin.passwordPlaceholderEdit': 'Leer lassen, um es unverändert zu lassen',
    'common.invoiceStatus.posted': 'Erfolgreich verbucht',
    'common.invoiceStatus.success': 'Erfolgreich verarbeitet',
    'common.invoiceStatus.manual_review': 'Prüfung',
    'common.invoiceStatus.error': 'Fehler',
    'common.invoiceStatus.processing': 'In Arbeit',
    'common.invoiceStatus.queued': 'Wartet',
    'common.role.admin': 'Admin',
    'common.role.operator': 'Operator',
    'common.role.customer-operator': 'Tenant-Operator',
    'common.status.active': 'Aktiv',
    'common.status.disabled': 'Deaktiviert',
  },
  en: {
    'shell.nav.invoices': 'Invoices',
    'shell.nav.rules': 'Rules',
    'shell.nav.admin': 'Admin',
    'shell.nav.orderConfirmations': 'Order confirmations',
    'shell.nav.customerOrders': 'Customer orders',
    'shell.section.supplierInvoices': 'Supplier invoices',
    'shell.section.orderConfirmations': 'Order confirmations',
    'shell.section.customerOrders': 'Customer orders',
    'shell.section.admin': 'Admin',
    'shell.sidebar.open': 'Open sidebar',
    'shell.sidebar.close': 'Close sidebar',
    'shell.notifications': 'Notifications',
    'shell.notificationsPage': 'Show notifications',
    'shell.mobileMenu': 'Mobile menu',
    'shell.mobileNav': 'Mobile navigation',
    'shell.mainNav': 'Main navigation',
    'shell.userMenu': 'User menu',
    'shell.userMenuOptions': 'User menu options',
    'shell.settings': 'Settings',
    'shell.logout': 'Log out',
    'shell.logoutPending': 'Logging out...',
    'shell.sourceOfTruth': 'Source of truth:',
    'shell.build': ({ mode }: Params) => `Invoice Automation Build ${mode}`,
    'auth.redirectTitle': 'Redirecting to login…',
    'auth.redirectMessage': 'Please sign in to load this view. You are being redirected.',
    'notFound.title': 'Page not found',
    'notFound.description': 'The requested route does not exist. Switch to Invoices to continue.',
    'login.title': 'Sign in',
    'login.description': 'After a successful login you will land directly in the invoice view.',
    'login.email': 'Email',
    'login.password': 'Password',
    'login.submit': 'Sign in',
    'login.invalidEmail': 'Please enter a valid email address',
    'login.passwordRequired': 'Password is required',
    'login.showPassword': 'Show password',
    'login.hidePassword': 'Hide password',
    'login.mfaUnsupported':
      'This user requires MFA. Completing the MFA flow is not wired into the web app yet.',
    'login.seedUser': 'Local seed user:',
    'settings.sectionLabel': 'Settings',
    'settings.pageTitle': 'Settings',
    'settings.pageDescription':
      'Manage your personal account preferences and your preferred language.',
    'settings.loading': 'Loading settings...',
    'settings.unauthorized': 'Please sign in to load your settings.',
    'settings.accountTitle': 'My account',
    'settings.accountDescription': 'Your user information and personal preferences.',
    'settings.languageTitle': 'Language',
    'settings.languageDescription':
      'This language is stored as your personal preference for the web interface.',
    'settings.languageLabel': 'Interface language',
    'settings.languageOption.de': 'German',
    'settings.languageOption.en': 'English',
    'settings.defaultTenantTitle': 'Default tenant',
    'settings.defaultTenantDescription':
      'Controls which tenant is preselected automatically in tenant-scoped views.',
    'settings.defaultTenantLabel': 'Default tenant',
    'settings.defaultTenantEmpty': 'No tenant available',
    'settings.defaultTenantAdminHint':
      'Administrators intentionally start without a preselected tenant.',
    'settings.saveSuccess': 'Settings saved.',
    'settings.saveFailed': 'Saving failed.',
    'settings.save': 'Save',
    'settings.saving': 'Saving...',
    'modulePlaceholder.authRequired': 'Please sign in to load this area.',
    'modulePlaceholder.loading': 'Loading area...',
    'orderConfirmations.sectionLabel': 'Workspace',
    'orderConfirmations.pageTitle': 'Order confirmations',
    'orderConfirmations.pageDescription':
      'Incoming order confirmations with SAP validation against ZEL1 purchase orders, delivery address, positions, and material master data.',
    'orderConfirmations.newConfirmation': 'New order confirmation',
    'orderConfirmations.newConfirmationAria': 'Upload new order confirmation',
    'orderConfirmations.loadingTitle': 'Loading order confirmations…',
    'orderConfirmations.count': ({ count }: Params) =>
      `${count} order confirmation${Number(count) === 1 ? '' : 's'}`,
    'orderConfirmations.loadError': 'Loading failed',
    'orderConfirmations.errorTitle': 'Order confirmations could not be loaded',
    'orderConfirmationRules.sectionLabel': 'Rules',
    'orderConfirmationRules.pageTitle': 'Order confirmation rules',
    'orderConfirmationRules.pageDescription':
      'Rules for processing order confirmations are collected here.',
    'customerOrders.sectionLabel': 'Workspace',
    'customerOrders.pageTitle': 'Customer orders',
    'customerOrders.pageDescription':
      'Incoming customer orders with LLM extraction, SAP customer lookup, KNMT material matching, and VA01 creation as ZAN1 scheduled orders.',
    'customerOrders.newOrder': 'New customer order',
    'customerOrders.newOrderAria': 'Upload new customer order',
    'customerOrders.loadingTitle': 'Loading customer orders…',
    'customerOrders.count': ({ count }: Params) =>
      `${count} customer order${Number(count) === 1 ? '' : 's'}`,
    'customerOrders.loadError': 'Loading failed',
    'customerOrders.errorTitle': 'Customer orders could not be loaded',
    'customerOrdersTable.searchLabel': 'Search customer',
    'customerOrdersTable.searchPlaceholder': 'e.g. Example Inc.',
    'customerOrdersTable.columnNumber': 'Order number',
    'customerOrdersTable.columnCustomer': 'Customer',
    'customerOrdersTable.columnAmount': 'Order value',
    'customerOrdersTable.columnTradingMargin': 'Trading margin',
    'customerOrdersTable.columnIssueDate': 'Order date',
    'customerOrdersTable.emptyTitle': 'No customer orders yet',
    'customerOrdersTable.emptyDescription':
      'Upload a customer order to extract fields with the LLM and prepare SAP creation.',
    'customerOrdersTable.selectAllAria': 'Select all customer orders',
    'customerOrdersTable.selectOrderAria': ({ order }: Params) => `Select customer order ${order}`,
    'customerOrdersTable.selectionCount': ({ count }: Params) =>
      `${count} customer order${Number(count) === 1 ? '' : 's'} selected`,
    'customerOrdersTable.bulkDeleteTitle': 'Delete customer orders',
    'customerOrdersTable.bulkDeleteDescription': ({ count }: Params) =>
      `Delete ${count} customer order${Number(count) === 1 ? '' : 's'}?`,
    'customerOrderRules.sectionLabel': 'Rules',
    'customerOrderRules.pageTitle': 'Customer order rules',
    'customerOrderRules.pageDescription':
      'Rules for processing customer orders are collected here.',
    'rules.sectionLabel': 'Rules',
    'rules.pageTitle': 'Mappings & Rules',
    'rules.pageDescription':
      'Tenant-generic and supplier-specific rules control the LLM extraction. Changes are versioned and can be restored from the versions panel.',
    'rules.create': 'New rule',
    'rules.createDisabledNoTenant': 'Please select a tenant first.',
    'rules.tab.generic': 'Tenant-generic',
    'rules.tab.customer': 'Supplier-specific',
    'rules.tenantLabel': 'Tenant',
    'rules.supplierSearchLabel': 'Search supplier',
    'rules.tenantPlaceholder': 'Search tenant name',
    'rules.tenantLoading': 'Searching tenants...',
    'rules.tenantEmpty': 'No tenants found.',
    'rules.tenantMinSearch': 'Click into the field to show all tenants, or type to filter.',
    'rules.supplierIdleHint': 'Click into the field to show all suppliers, or type to filter.',
    'rules.loading': 'Loading rules…',
    'rules.count': ({ count }: Params) => `${count} rule${Number(count) === 1 ? '' : 's'}`,
    'rules.loadError': 'Loading failed',
    'rules.selectTenantHint': 'Select a tenant to display rules.',
    'rules.deleteConfirm': ({ title }: Params) => `Delete rule "${title}"?`,
    'rulesTable.title': 'Title',
    'rulesTable.tenant': 'Tenant',
    'rulesTable.status': 'Status',
    'rulesTable.version': 'Version',
    'rulesTable.lastEditor': 'Last editor',
    'rulesTable.updated': 'Updated',
    'rulesTable.actions': 'Actions',
    'rulesTable.active': 'Active',
    'rulesTable.inactive': 'Inactive',
    'rulesTable.activate': 'Activate',
    'rulesTable.deactivate': 'Deactivate',
    'rulesTable.edit': 'Edit',
    'rulesTable.testRun': 'Test run',
    'rulesTable.delete': 'Delete',
    'rulesTable.editAria': ({ title }: Params) => `Edit rule ${title}`,
    'rulesTable.testRunAria': ({ title }: Params) => `Test rule ${title}`,
    'rulesTable.deleteAria': ({ title }: Params) => `Delete rule ${title}`,
    'rulesTable.toggleActiveAria': 'Activate rule',
    'rulesTable.toggleInactiveAria': 'Deactivate rule',
    'rulesTable.emptyTitle': 'No rules yet',
    'rulesTable.emptyDescription': 'Create the first rule to configure extraction.',
    'ruleDialog.headerEdit': ({ version }: Params) => `Edit rule – v${version}`,
    'ruleDialog.headerCreate': 'New rule',
    'ruleDialog.descriptionEdit':
      'Edits the active version. The previous version is stored automatically in history.',
    'ruleDialog.descriptionCreate':
      'Creates a new rule. Supplier-specific rules override tenant-generic rules on conflicts.',
    'ruleDialog.tabEdit': 'Edit',
    'ruleDialog.tabVersions': 'Versions',
    'ruleDialog.scopeLabel': 'Scope',
    'ruleDialog.scopeGeneric': 'Tenant-generic',
    'ruleDialog.scopeCustomer': 'Supplier-specific',
    'ruleDialog.supplierLabel': 'Supplier',
    'ruleDialog.supplierPlaceholder': 'Search supplier name',
    'ruleDialog.supplierIdleHint':
      'Select a tenant first; then the field shows all known suppliers.',
    'ruleDialog.supplierLoading': 'Loading suppliers...',
    'ruleDialog.supplierEmpty': 'No suppliers found.',
    'ruleDialog.supplierClearAria': 'Clear supplier',
    'ruleDialog.titleLabel': 'Title *',
    'ruleDialog.bodyLabel': 'Body *',
    'ruleDialog.bodyAria': 'Rule body',
    'ruleDialog.characters': ({ count, max }: Params) =>
      `${Number(count).toLocaleString('en-US')} / ${Number(max).toLocaleString('en-US')} characters`,
    'ruleDialog.activeLabel': 'Active',
    'ruleDialog.delete': 'Delete',
    'ruleDialog.testRun': 'Test run',
    'ruleDialog.cancel': 'Cancel',
    'ruleDialog.save': 'Save',
    'ruleDialog.create': 'Create',
    'ruleDialog.deleteTitle': 'Delete rule?',
    'ruleDialog.deleteDescription':
      'The rule will be set inactive and removed from active editing.',
    'ruleDialog.titleRequired': 'Title is required',
    'ruleDialog.titleMax': 'Maximum 200 characters',
    'ruleDialog.bodyRequired': 'Body is required',
    'ruleDialog.bodyMax': ({ max }: Params) => `Maximum ${max} characters`,
    'ruleDialog.tenantRequired': 'Tenant is required',
    'ruleDialog.supplierRequired': 'Supplier is required',
    'upload.title': 'Upload invoices',
    'upload.description': 'PDF or XML. Maximum 20 MB per file. Multiple files are supported.',
    'upload.orderConfirmationTitle': 'Upload order confirmations',
    'upload.orderConfirmationDescription':
      'PDF or XML. Processing validates purchase order number, positions, delivery address, and material numbers against SAP.',
    'upload.customerOrderTitle': 'Upload customer orders',
    'upload.customerOrderDescription':
      'PDF or XML. Processing extracts customer orders, calculates trading margin, and prepares VA01 creation.',
    'upload.tenantLabel': 'Tenant',
    'upload.tenantSelectAuto': 'Tenant is selected automatically',
    'upload.tenantSelectPlaceholder': 'Select tenant',
    'upload.tenantAutoHint':
      'If no tenant is selected, the upload automatically uses the only visible tenant.',
    'upload.tenantLoadError': ({ message }: Params) => `Unable to load tenants: ${message}`,
    'upload.tenantRequiredHint': 'Please select a tenant before processing.',
    'upload.tenantRequiredFlash': 'Please select a tenant first or use the only available tenant.',
    'upload.fileInputLabel': 'Select files',
    'upload.fileListLabel': 'Files to upload',
    'upload.cancel': 'Cancel',
    'upload.processCount': ({ count }: Params) => `Process (${count})`,
    'upload.pickFiles': 'Select files',
    'upload.dropTitle': 'Drop files here',
    'upload.dropDescription': 'PDF, XML – maximum 20 MB per file',
    'upload.closeRunningTitle': 'Cancel upload?',
    'upload.closeRunningDescription':
      'Uploads are still running. Closing now discards the local overview.',
    'upload.closeRunningConfirm': 'Abort upload',
    'upload.closeRunningStay': 'Keep uploading',
    'upload.flashSuccess': ({ successCount }: Params) =>
      `${successCount} file(s) uploaded successfully.`,
    'upload.flashError': ({ errorCount }: Params) =>
      `${errorCount} file(s) failed. Use "Retry" to try again.`,
    'upload.flashMixed': ({ successCount, errorCount }: Params) =>
      `${successCount} uploaded, ${errorCount} failed. Failed files can be retried.`,
    'upload.invalidFile': 'Invalid file',
    'uploadRow.retryAria': ({ name }: Params) => `Retry: ${name}`,
    'uploadRow.removeAria': ({ name }: Params) => `Remove: ${name}`,
    'uploadRow.phaseQueued': 'Ready',
    'uploadRow.phaseUploading': 'Uploading …',
    'uploadRow.phaseExisting': 'Already exists',
    'uploadRow.phaseUploaded': 'Uploaded',
    'uploadRow.phaseError': 'Error',
    'invoiceList.sectionLabel': 'Workspace',
    'invoiceList.pageTitle': 'Invoices',
    'invoiceList.pageDescription':
      'All incoming invoices with status, supplier, and amount. Filters and sorting are stored in the URL and remain shareable.',
    'invoiceList.newInvoice': 'New invoice',
    'invoiceList.newInvoiceAria': 'Upload new invoice',
    'invoiceList.loadingTitle': 'Loading invoices…',
    'invoiceList.count': ({ count }: Params) => `${count} invoices`,
    'invoiceList.loadError': 'Loading failed',
    'invoiceList.errorTitle': 'Invoices could not be loaded',
    'invoiceList.retry': 'Retry',
    'invoiceListTable.columnNumber': 'Invoice number',
    'invoiceListTable.columnDocumentType': 'Document type',
    'invoiceListTable.columnTenant': 'Tenant',
    'invoiceListTable.columnSupplier': 'Supplier',
    'invoiceListTable.columnAmount': 'Amount',
    'invoiceListTable.columnTradingMargin': 'Trading margin',
    'invoiceListTable.columnIssueDate': 'Invoice date',
    'invoiceListTable.columnStatus': 'Status',
    'invoiceListTable.columnUploaded': 'Uploaded',
    'invoiceListTable.skontoUntil': ({ date }: Params) => `Discount until ${date}`,
    'invoiceListTable.columnActions': 'Actions',
    'invoiceListTable.selectAllAria': 'Select all deletable invoices',
    'invoiceListTable.selectInvoiceAria': ({ invoice }: Params) => `Select invoice ${invoice}`,
    'invoiceListTable.selectionCount': ({ count }: Params) =>
      `${count} invoice${Number(count) === 1 ? '' : 's'} selected`,
    'invoiceListTable.clearSelection': 'Clear selection',
    'invoiceListTable.bulkDeleteAction': 'Delete selected',
    'invoiceListTable.bulkDeleteTitle': 'Delete selected invoices?',
    'invoiceListTable.bulkDeleteDescription': ({ count }: Params) =>
      `This permanently removes ${count} invoice${Number(count) === 1 ? '' : 's'} and their files.`,
    'invoiceListTable.sortBy': ({ label }: Params) => `Sort by ${label}`,
    'invoiceListTable.unknownNumber': 'unknown',
    'invoiceListTable.documentTypeInvoice': 'Invoice',
    'invoiceListTable.documentTypeOrderConfirmation': 'Order confirmation',
    'invoiceListTable.documentTypeUnknown': 'Unknown',
    'invoiceListTable.emptyTitle': 'No invoices yet',
    'invoiceListTable.emptyDescription':
      'As soon as the first invoices are uploaded, they appear here with status, amount, and supplier.',
    'invoiceListTable.deleteTitle': 'Delete invoice?',
    'invoiceListTable.deleteDescription': 'This permanently removes the file and the record.',
    'invoiceListTable.deleteAction': 'Delete',
    'invoiceListTable.deleteAria': ({ invoice }: Params) => `Delete invoice ${invoice}`,
    'orderConfirmationsTable.columnNumber': 'Confirmation number',
    'orderConfirmationsTable.columnIssueDate': 'Confirmation date',
    'orderConfirmationsTable.emptyTitle': 'No order confirmations yet',
    'orderConfirmationsTable.emptyDescription':
      'Once order confirmations are processed by email or upload, they appear here with status, amount, and supplier.',
    'orderConfirmationsTable.selectAllAria': 'Select all deletable order confirmations',
    'orderConfirmationsTable.selectConfirmationAria': ({ confirmation }: Params) =>
      `Select order confirmation ${confirmation}`,
    'orderConfirmationsTable.selectionCount': ({ count }: Params) =>
      `${count} order confirmation${Number(count) === 1 ? '' : 's'} selected`,
    'orderConfirmationsTable.bulkDeleteTitle': 'Delete selected order confirmations?',
    'orderConfirmationsTable.bulkDeleteDescription': ({ count }: Params) =>
      `This permanently removes ${count} order confirmation${Number(count) === 1 ? '' : 's'} and their files.`,
    'comments.title': 'Comments',
    'comments.loading': 'Loading comments...',
    'comments.empty': 'No comments yet.',
    'comments.newLabel': 'New comment',
    'comments.placeholder': 'Markdown supported: **bold**, *italic*, `code`, [link](https://...)',
    'comments.publish': 'Publish',
    'comments.characters': ({ count, max }: Params) => `${count} / ${max}`,
    'comments.errorEmpty': 'Please enter a comment.',
    'comments.errorSave': 'Comment could not be saved.',
    'comments.errorDelete': 'Comment could not be deleted.',
    'comments.errorForbiddenDelete': 'Only the author or an admin may delete this comment.',
    'comments.deleteAria': 'Delete comment',
    'comments.delete': 'Delete',
    'invoiceEdit.unsavedChanges': 'Unsaved changes',
    'invoiceEdit.allSaved': 'All changes saved',
    'invoiceEdit.save': 'Save',
    'invoiceEdit.saveAria': 'Save changes',
    'invoiceEdit.metaSection': 'Document data',
    'invoiceEdit.documentTypeLabel': 'Document type',
    'invoiceEdit.documentTypeInvoice': 'Invoice',
    'invoiceEdit.documentTypeOrderConfirmation': 'Order confirmation',
    'invoiceEdit.orderNumberLabelInvoice': 'Internal purchase order number',
    'invoiceEdit.orderNumberLabelOrderConfirmation': 'Purchase order number',
    'invoiceEdit.orderDateLabel': 'Order date',
    'invoiceEdit.documentNumberLabelInvoice': 'Invoice number',
    'invoiceEdit.documentNumberLabelOrderConfirmation': 'Order confirmation number',
    'invoiceEdit.issueDateLabel': 'Document date',
    'invoiceEdit.dueDateLabel': 'Due date',
    'invoiceEdit.deliveryDateLabel': 'Delivery date',
    'invoiceEdit.currencyLabel': 'Currency',
    'invoiceEdit.paymentSection': 'Payment terms & discount',
    'invoiceEdit.deliveryTermsSection': 'Delivery terms',
    'invoiceEdit.paymentTermsLabel': 'Payment terms',
    'invoiceEdit.discountDeadlineLabel': 'Discount until',
    'invoiceEdit.discountPercentLabel': 'Discount in %',
    'invoiceEdit.paymentDueDateLabel': 'Due without deduction',
    'invoiceEdit.discountAmountLabel': 'Discount in €',
    'invoiceEdit.amountAfterDiscountLabel': 'Final amount after discount',
    'invoiceEdit.incotermsLabel': 'Incoterms',
    'invoiceEdit.sellerSection': 'Supplier',
    'invoiceEdit.buyerSection': 'Buyer',
    'invoiceEdit.nameLabel': 'Name',
    'invoiceEdit.streetLabel': 'Street',
    'invoiceEdit.postalCodeLabel': 'Postal code',
    'invoiceEdit.cityLabel': 'City',
    'invoiceEdit.countryLabel': 'Country (ISO-2)',
    'invoiceEdit.countryPlaceholder': 'DE',
    'invoiceEdit.vatIdLabel': 'VAT ID',
    'invoiceEdit.vatIdPlaceholder': 'DE123456789',
    'invoiceEdit.sapSupplierIdLabel': 'SAP supplier ID',
    'invoiceEdit.sapCustomerIdLabel': 'SAP customer ID',
    'invoiceEdit.sapChecking': 'Checking SAP supplier...',
    'invoiceEdit.sapMapped': ({ id }: Params) => `Derived from an existing SAP mapping: ${id}`,
    'invoiceEdit.sapNotFound':
      'No existing SAP mapping was found. Please complete the supplier ID manually.',
    'invoiceEdit.itemsSection': 'Line items',
    'invoiceEdit.ancillaryItemsSection': 'Ancillary cost line items',
    'invoiceEdit.addPosition': 'Add line item',
    'invoiceEdit.addAncillaryPosition': 'Add ancillary cost',
    'invoiceEdit.noItems': 'No line items captured.',
    'invoiceEdit.noAncillaryItems': 'No ancillary cost line items captured.',
    'invoiceEdit.positionNumber': ({ index }: Params) => `Line item ${index}`,
    'invoiceEdit.removePositionAria': ({ index }: Params) => `Remove line item ${index}`,
    'invoiceEdit.positionLabel': 'Pos.',
    'invoiceEdit.itemTypeLabel': 'Line item type',
    'invoiceEdit.itemTypeStandard': 'Standard line item',
    'invoiceEdit.itemTypeAncillary': 'Ancillary cost line item',
    'invoiceEdit.articleNumberLabel': 'Material number',
    'invoiceEdit.sapMaterialNumberLabel': 'SAP material number',
    'invoiceEdit.sapMaterialMissing': 'SAP material missing:',
    'invoiceEdit.sapMaterialMultiple': 'SAP material is ambiguous:',
    'invoiceEdit.descriptionLabel': 'Description',
    'invoiceEdit.quantityLabel': 'Quantity',
    'invoiceEdit.unitPriceLabel': 'Unit price',
    'invoiceEdit.lineDiscountPercentLabel': 'Discount in %',
    'invoiceEdit.discountAmountLineLabel': 'Discount',
    'invoiceEdit.vatRateLabel': 'VAT rate %',
    'invoiceEdit.netLabel': 'Net',
    'invoiceEdit.totalsSection': 'Totals',
    'invoiceEdit.vatLabel': 'VAT',
    'invoiceEdit.grossLabel': 'Gross',
    'invoiceEdit.fieldRequired': 'Required field is missing',
    'invoiceEdit.fieldInvalid': 'Invalid value',
    'invoiceEdit.numberInvalid': 'Please enter a valid number',
    'invoiceEdit.dateInvalid': 'Please enter a valid date',
    'invoiceEdit.vatIdInvalid': 'Invalid VAT ID format',
    'invoiceEdit.ibanInvalidFormat':
      'Invalid IBAN format (country code + 2 check digits + BBAN expected)',
    'invoiceEdit.ibanInvalidCountryLength': 'IBAN length does not match the country',
    'invoiceEdit.ibanInvalidChecksum': 'IBAN checksum does not match (MOD-97)',
    'invoiceEdit.ibanInvalid': 'IBAN is invalid',
    'supplierRule.button': 'Add rule',
    'supplierRule.scopeTitle': 'Supplier-specific rule',
    'supplierRule.closeAria': 'Close',
    'supplierRule.placeholder': 'Describe the supplier-specific rule for the prompt here.',
    'supplierRule.cancel': 'Cancel',
    'supplierRule.save': 'Save',
    'supplierRule.saving': 'Saving...',
    'supplierRule.saveError': 'Rule could not be saved.',
    'supplierRule.title': ({ supplierName }: Params) => `Supplier rule – ${supplierName}`,
    'notifications.sectionLabel': 'Notifications',
    'notifications.pageTitle': 'Notifications',
    'notifications.pageDescription':
      'All manual changes and processing errors for the tenants visible to you.',
    'notifications.loading': 'Loading notifications...',
    'notifications.emptyTitle': 'No notifications yet',
    'notifications.emptyDescription':
      'Errors and manual invoice adjustments will appear here once they happen.',
    'notifications.loadError': 'Notifications could not be loaded.',
    'notifications.dropdownTitle': 'Latest notifications',
    'notifications.dropdownEmpty': 'No new entries.',
    'notifications.searchLabel': 'Search',
    'notifications.searchPlaceholder': 'Search by invoice, tenant, or text',
    'notifications.tenantFilterLabel': 'Tenant',
    'notifications.tenantFilter.all': 'All tenants',
    'notifications.typeFilterLabel': 'Type',
    'notifications.typeFilter.all': 'All types',
    'notifications.typeFilter.invoice_processed': 'Processed',
    'notifications.typeFilter.invoice_failed': 'Failed',
    'notifications.typeFilter.invoice_manual_review': 'Manual review',
    'notifications.typeFilter.invoice_comment_created': 'Comment',
    'notifications.typeFilter.invoice_updated': 'Manual update',
    'notifications.typeFilter.invoice_status_changed': 'Status change',
    'notifications.typeFilter.invoice_skonto_reminder': 'Discount reminder',
    'notifications.typeFilter.rule_applied': 'Rule applied',
    'notifications.typeFilter.system': 'System',
    'notifications.viewAll': 'View all',
    'notifications.clearAll': 'Clear',
    'notifications.clearing': 'Clearing...',
    'notifications.delete': 'Delete',
    'notifications.deleting': 'Deleting...',
    'notifications.level.info': 'Info',
    'notifications.level.error': 'Error',
    'notifications.level.warning': 'Review',
    'notifications.read': 'Read',
    'notifications.unread': 'Unread',
    'notifications.openInvoice': 'Open invoice',
    'notifications.someone': 'Someone',
    'notifications.invoiceUpdatedTitle': 'Manual invoice update',
    'notifications.invoiceUpdatedMessage': ({ actor, invoice }: Params) =>
      `${actor} manually updated ${invoice}.`,
    'notifications.commentCreatedTitle': 'New comment',
    'notifications.commentCreatedMessage': ({ actor, invoice }: Params) =>
      `${actor} commented on ${invoice}.`,
    'notifications.statusChangedTitle': 'Status changed manually',
    'notifications.statusChangedMessage': ({ actor, from, to }: Params) =>
      `${actor} changed the status from ${from} to ${to}.`,
    'notifications.manualReviewTitle': 'Manual review required',
    'notifications.manualReviewMessage': ({ invoice, details }: Params) =>
      `${invoice} requires manual review.${details ? ` ${details}` : ''}`,
    'notifications.failedTitle': 'Processing failed',
    'notifications.failedMessage': ({ invoice, details }: Params) =>
      `${invoice} could not be processed.${details ? ` ${details}` : ''}`,
    'notifications.skontoReminderTitle': 'Discount deadline approaching',
    'notifications.skontoReminderMessage': ({ invoice, deadline }: Params) =>
      `${invoice} should be paid by ${deadline} to secure the discount.`,
    'common.name': 'Name',
    'common.email': 'Email',
    'common.role': 'Role',
    'common.status': 'Status',
    'common.language': 'Language',
    'common.selected': 'Selected',
    'common.clear': 'clear',
    'admin.forbidden': 'This view is restricted to admins.',
    'admin.passwordPlaceholderEdit': 'Leave empty to keep the current password',
    'common.invoiceStatus.posted': 'Posted successfully',
    'common.invoiceStatus.success': 'Processed successfully',
    'common.invoiceStatus.manual_review': 'Review',
    'common.invoiceStatus.error': 'Error',
    'common.invoiceStatus.processing': 'Processing',
    'common.invoiceStatus.queued': 'Queued',
    'common.role.admin': 'Admin',
    'common.role.operator': 'Operator',
    'common.role.customer-operator': 'Tenant operator',
    'common.status.active': 'Active',
    'common.status.disabled': 'Disabled',
  },
} as const satisfies Record<UserLanguage, Record<string, MessageValue>>;

type MessageKey = keyof (typeof messages)['de'];

interface I18nContextValue {
  language: UserLanguage;
  setLanguage: (language: UserLanguage) => void;
  t: (key: MessageKey, params?: Params) => string;
  formatDateTime: (value: string) => string;
  roleLabel: (role: UserRole) => string;
  statusLabel: (status: UserStatus) => string;
}

const I18nContext = createContext<I18nContextValue>({
  language: 'de',
  setLanguage: () => undefined,
  t: (key, params) => resolveMessage('de', key, params),
  formatDateTime: (value) => formatDateTimeForLanguage('de', value),
  roleLabel: (role) => resolveMessage('de', `common.role.${role}` as MessageKey),
  statusLabel: (status) => resolveMessage('de', `common.status.${status}` as MessageKey),
});

export function I18nProvider({
  language,
  children,
}: {
  language: UserLanguage;
  children: ReactNode;
}) {
  const value = useMemo<I18nContextValue>(
    () => ({
      language,
      setLanguage: () => undefined,
      t: (key, params) => resolveMessage(language, key, params),
      formatDateTime: (value) => formatDateTimeForLanguage(language, value),
      roleLabel: (role) => resolveMessage(language, `common.role.${role}` as MessageKey),
      statusLabel: (status) => resolveMessage(language, `common.status.${status}` as MessageKey),
    }),
    [language],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function I18nAppProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<UserLanguage>(() => readInitialLanguage());

  const setLanguage = useCallback((nextLanguage: UserLanguage) => {
    setLanguageState(nextLanguage);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_KEY, nextLanguage);
    }
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({
      language,
      setLanguage,
      t: (key, params) => resolveMessage(language, key, params),
      formatDateTime: (value) => formatDateTimeForLanguage(language, value),
      roleLabel: (role) => resolveMessage(language, `common.role.${role}` as MessageKey),
      statusLabel: (status) => resolveMessage(language, `common.status.${status}` as MessageKey),
    }),
    [language, setLanguage],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}

function resolveMessage(language: UserLanguage, key: MessageKey, params: Params = {}): string {
  const message = messages[language][key];
  if (typeof message === 'function') {
    return message(params);
  }
  return message;
}

function formatDateTimeForLanguage(language: UserLanguage, value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'de-DE', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
}

function readInitialLanguage(): UserLanguage {
  if (typeof window !== 'undefined') {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'de' || stored === 'en') return stored;
    const browserLanguage = window.navigator.language.toLowerCase();
    if (browserLanguage.startsWith('en')) return 'en';
  }
  return 'de';
}
